import { supabase } from '../lib/supabase';
import { TeamMember, TeamInvite } from './useTeamMembers';

interface DeleteResult {
  success: boolean;
  error?: string;
}

interface InviteResult {
  success: boolean;
  error?: string;
}

interface LanguageResult {
  success: boolean;
  error?: string;
}

export const useTeamActions = () => {
  const deleteTeamMember = async (
    teamId: string,
    memberId: string,
    currentMembers: TeamMember[]
  ): Promise<DeleteResult> => {
    try {
      const { error } = await supabase
        .from('Teams')
        .delete()
        .eq('id', teamId)
        .eq('member_profile_id', memberId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const deleteTeamInvite = async (inviteId: string): Promise<void> => {
    const { error } = await supabase
      .from('UserInvites')
      .delete()
      .eq('id', inviteId);

    if (error) throw error;
  };

  const inviteTeamMember = async (
    teamId: string,
    email: string,
    currentMembers: TeamMember[],
    currentInvites: TeamInvite[]
  ): Promise<InviteResult> => {
    try {
      // Check if user is already a member
      const isMember = currentMembers.some(member => member.email === email);
      if (isMember) {
        throw new Error('User is already a team member');
      }

      // Check if user already has a pending invite
      const hasInvite = currentInvites.some(invite => invite.email === email);
      if (hasInvite) {
        throw new Error('User already has a pending invite');
      }

      const { error } = await supabase
        .from('UserInvites')
        .insert([
          {
            team_id: teamId,
            email: email
          }
        ]);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to send invite. Please try again.'
      };
    }
  };

  const deleteLanguage = async (projectId: string, languageCode: string): Promise<void> => {
    const { error } = await supabase
      .from('TranslationStrings')
      .delete()
      .eq('project_id', projectId)
      .eq('language_code', languageCode);

    if (error) throw error;
  };

  const updateLanguages = async (projectId: string, languages: string[]): Promise<void> => {
    const { error } = await supabase
      .from('Projects')
      .update({ languages })
      .eq('id', projectId);

    if (error) throw error;
  };

  const deleteLanguageFromProject = async (projectId: string, languageCode: string): Promise<void> => {
    // First delete all translation strings for this language
    const { error: stringsError } = await supabase
      .from('TranslationStrings')
      .delete()
      .eq('project_id', projectId)
      .eq('language_code', languageCode);

    if (stringsError) throw stringsError;

    // Then update the project's languages array
    const { data: project, error: projectError } = await supabase
      .from('Projects')
      .select('languages')
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    const updatedLanguages = project.languages.filter((lang: string) => lang !== languageCode);
    const { error: updateError } = await supabase
      .from('Projects')
      .update({ languages: updatedLanguages })
      .eq('id', projectId);

    if (updateError) throw updateError;
  };

  return {
    deleteTeamMember,
    deleteTeamInvite,
    inviteTeamMember,
    deleteLanguage,
    updateLanguages,
    deleteLanguageFromProject
  };
}; 